import { useHead } from '@unhead/vue'
import { useRoute } from 'vue-router'

/**
 * Core SEO constants - single source of truth
 */
export const SEO_CONFIG = {
  siteName: 'Otivo',
  domain: 'https://www.otivo.com',
  defaultTitle: 'Otivo: Personalised & Affordable Financial Advice for Australians',
  defaultDescription:
    'Get smarter with your money. Otivo offers easy-to-use online financial advice to help Australians plan, save, and grow their wealth.',
  defaultImage: 'https://www.otivo.com/img/otivo-logo-white-text.png',
  twitterHandle: '@OtivoFinance',
  contactPhone: '+61-2-8084-0827',
  address: {
    street: 'Suite 2.01, 50 York Street',
    city: 'Sydney',
    state: 'NSW',
    postcode: '2000',
    country: 'AU',
  },
  socialMedia: {
    facebook: 'https://www.facebook.com/OtivoFinance',
    twitter: 'https://twitter.com/OtivoFinance',
    linkedin: 'https://www.linkedin.com/company/otivo',
    youtube: 'https://www.youtube.com/channel/UCqZlQ8V8pGrKqZVG8hQb6ZQ',
  },
} as const

/**
 * Formats page title with consistent Otivo branding
 * @param title - Page title (e.g., "Contact Us")
 * @returns Formatted title (e.g., "Contact Us | Otivo")
 */
export const formatTitle = (title: string): string => {
  if (!title || typeof title !== 'string') return SEO_CONFIG.defaultTitle
  return title.includes('Otivo') ? title : `${title} | ${SEO_CONFIG.siteName}`
}

/**
 * Generates canonical URL for current page
 * @param path - Optional custom path, defaults to current route
 * @returns Full canonical URL
 */
export const getCanonicalUrl = (path?: string): string => {
  if (path !== undefined) {
    // Handle empty string case
    if (path === '') return SEO_CONFIG.domain
    // Ensure path starts with /
    const normalizedPath = path.startsWith('/') ? path : `/${path}`
    return `${SEO_CONFIG.domain}${normalizedPath}`
  }
  const route = useRoute()
  return `${SEO_CONFIG.domain}${route.path}`
}

/**
 * Creates standard meta tags that every page should have
 * @param options - SEO options
 * @returns Object with title, meta, and link arrays for useHead()
 */
export const createStandardMeta = (options: {
  title: string
  description: string
  keywords?: string
  image?: string
  type?: 'website' | 'article'
  noindex?: boolean
  canonical?: string
}) => {
  const {
    title,
    description,
    keywords,
    image = SEO_CONFIG.defaultImage,
    type = 'website',
    noindex = false,
    canonical,
  } = options

  const formattedTitle = formatTitle(title)
  const canonicalUrl = canonical || getCanonicalUrl()

  return {
    title: formattedTitle,
    meta: [
      // Basic meta tags
      { name: 'description', content: description },
      ...(keywords ? [{ name: 'keywords', content: keywords }] : []),
      { name: 'robots', content: noindex ? 'noindex,nofollow' : 'index,follow' },

      // Open Graph tags
      { property: 'og:title', content: formattedTitle },
      { property: 'og:description', content: description },
      { property: 'og:image', content: image },
      { property: 'og:url', content: canonicalUrl },
      { property: 'og:type', content: type },
      { property: 'og:site_name', content: SEO_CONFIG.siteName },

      // Twitter Card tags
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:site', content: SEO_CONFIG.twitterHandle },
      { name: 'twitter:title', content: formattedTitle },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: image },
    ],
    link: [{ rel: 'canonical', href: canonicalUrl }],
  }
}

/**
 * Standard organization schema used across all pages
 */
export const createOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SEO_CONFIG.siteName,
  url: SEO_CONFIG.domain,
  logo: SEO_CONFIG.defaultImage,
  sameAs: Object.values(SEO_CONFIG.socialMedia),
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: SEO_CONFIG.contactPhone,
    contactType: 'customer service',
    availableLanguage: 'English',
  },
  address: {
    '@type': 'PostalAddress',
    streetAddress: SEO_CONFIG.address.street,
    addressLocality: SEO_CONFIG.address.city,
    addressRegion: SEO_CONFIG.address.state,
    postalCode: SEO_CONFIG.address.postcode,
    addressCountry: SEO_CONFIG.address.country,
  },
})

/**
 * Creates breadcrumb schema from array of breadcrumb items
 * @param items - Breadcrumb items with name and optional URL
 * @returns Breadcrumb JSON-LD schema
 */
export const createBreadcrumbSchema = (items: Array<{ name: string; url?: string }>) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    ...(item.url && { item: `${SEO_CONFIG.domain}${item.url}` }),
  })),
})

/**
 * Adds JSON-LD structured data to page
 * @param schemas - One or more schema objects
 * @returns Object with script array for useHead()
 */
export const addStructuredData = (schemas: object | object[]) => {
  const schemaArray = Array.isArray(schemas) ? schemas : [schemas]
  return {
    script: [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify(schemaArray),
      },
    ],
  }
}
